import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { confirmAlert, toastMessageAlert } from "../../utils/alert";
import { useSearchParams } from "react-router-dom";
import Modal from "../../components/common/Modal";

const initialForm = {
  page: "about_us",
  section_key: "",
  heading: "",
  subheading: "",
  body: "",
  image_paths: "",
  image_layout: "auto",
  image_fit: "cover",
  image_size: "medium",
  layout: "stack",
  media_position: "top",
  heading_align: "left",
  text_align: "left",
  desktop_span: 1,
  show_heading: true,
  show_subheading: true,
  show_body: true,
  show_image: true,
  cta_label: "",
  cta_link: "",
  sort_order: 0,
  is_active: true,
  remove_image: false,
};
const MAX_MULTI_IMAGE_UPLOAD = 20;
const KNOWN_PAGE_KEYS = [
  "about_us",
  "home",
  "site_header",
  "events",
  "gallery",
  "players",
  "news",
  "updates_results",
  "donate",
  "messages",
  "volunteer",
  "partner",
  "chaitanya_yatra",
  "privacy_policy",
  "terms",
];
const SECTION_KEY_SUGGESTIONS = [
  "site_header_president",
  "home_hero",
  "home_about",
  "home_impact",
  "home_timeline",
  "home_leadership",
  "home_champions",
  "home_initiatives",
  "home_cta",
  "about_hero",
  "about_affiliated_meta",
  "donate_hero",
  "donate_why_meta",
  "donate_bank",
  "donate_upi",
  "messages_hero",
  "messages_leader_1",
  "updates_results_meta",
  "updates_result_sheet_1",
  "why_join_meta",
  "why_join_card_1",
];

const normalizeKeyToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizePageKey = (value) => normalizeKeyToken(value);
const normalizeSectionKeyInput = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_");

const inferPageFromSectionKey = (sectionKey) => {
  const key = normalizeSectionKeyInput(sectionKey);
  if (!key) return "";
  if (/^site_header_/.test(key)) return "site_header";
  if (/^(home_|why_join_)/.test(key)) return "home";
  if (/^about_/.test(key)) return "about_us";
  if (/^donate_/.test(key)) return "donate";
  if (/^messages_/.test(key)) return "messages";
  if (/^updates_/.test(key)) return "updates_results";
  if (/^news_/.test(key)) return "news";
  if (/^events_/.test(key)) return "events";
  if (/^gallery_/.test(key)) return "gallery";
  if (/^players_/.test(key)) return "players";
  if (/^volunteer_/.test(key)) return "volunteer";
  if (/^partner_/.test(key)) return "partner";
  if (/^privacy_policy_/.test(key)) return "privacy_policy";
  if (/^terms_/.test(key)) return "terms";
  if (/^chaitanya_yatra_/.test(key)) return "chaitanya_yatra";
  return "";
};

const CROP_PRESETS = {
  "16:9": {
    key: "16:9",
    label: "16:9 (Hero / Banner)",
    width: 1600,
    height: 900,
  },
  "4:3": {
    key: "4:3",
    label: "4:3 (Section blocks)",
    width: 1200,
    height: 900,
  },
  "1:1": {
    key: "1:1",
    label: "1:1 (Leadership photo)",
    width: 1000,
    height: 1000,
  },
  "3:4": {
    key: "3:4",
    label: "3:4 (Portrait / Champion)",
    width: 900,
    height: 1200,
  },
  "3:1": {
    key: "3:1",
    label: "3:1 (Wide card / initiative)",
    width: 1800,
    height: 600,
  },
};

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const clampRange = (value, min, max) => Math.min(max, Math.max(min, value));
const resolveSectionGalleryLayout = (layout, imageCount) => {
  const normalizedLayout = String(layout || "auto").toLowerCase();
  if (imageCount <= 1) return "single";
  if (["slider", "horizontal", "vertical", "grid2", "grid3"].includes(normalizedLayout)) {
    return normalizedLayout;
  }
  if (normalizedLayout === "auto") return imageCount <= 2 ? "grid2" : "slider";
  return "slider";
};

const getLayoutVisibilityHint = (layout, imageCount) => {
  const resolved = resolveSectionGalleryLayout(layout, imageCount);
  if (resolved === "single") {
    return "Shows 1 image at a time.";
  }
  if (resolved === "slider") {
    return `Shows 1 main image + thumbnail strip. Total uploaded: ${imageCount}.`;
  }
  if (resolved === "horizontal") {
    return `Horizontal scroll. About 2 cards visible on desktop (total ${imageCount}).`;
  }
  if (resolved === "vertical") {
    return `Vertical stack. About 1-2 cards visible without scrolling (total ${imageCount}).`;
  }
  if (resolved === "grid2") {
    return `Grid with 2 columns on desktop (total ${imageCount}).`;
  }
  return `Grid with 3 columns on desktop (total ${imageCount}).`;
};

const getApproxVisibleCount = (layout, imageCount) => {
  if (!imageCount) return 0;
  const resolved = resolveSectionGalleryLayout(layout, imageCount);
  if (resolved === "single" || resolved === "slider") return Math.min(1, imageCount);
  if (resolved === "horizontal") return Math.min(2, imageCount);
  if (resolved === "vertical") return Math.min(2, imageCount);
  if (resolved === "grid2") return Math.min(4, imageCount);
  return Math.min(6, imageCount);
};

const getPreviewImageClass = (fit) => {
  const normalized = String(fit || "cover").toLowerCase();
  if (["contain", "contain_width", "contain_height"].includes(normalized)) {
    return "object-contain bg-slate-100";
  }
  return "object-cover";
};
const getPreviewFrameSizeClass = (size) => {
  const normalized = String(size || "medium").toLowerCase();
  if (normalized === "small") return "max-w-[560px]";
  if (normalized === "large") return "max-w-[980px]";
  if (normalized === "xlarge") return "max-w-[1180px]";
  return "max-w-[760px]";
};

const getSectionPreviewProfile = (sectionKey) => {
  const key = String(sectionKey || "").toLowerCase();
  if (/^home_initiative_card_/.test(key)) {
    return {
      id: "home_initiative_card",
      label: "Home initiative card",
      note: "Live card uses image area `16:9` with configured image fit.",
    };
  }
  return {
    id: "default",
    label: "Generic section gallery",
    note: "Uses gallery layout preview based on image layout settings.",
  };
};

const drawCropToCanvas = (
  image,
  canvas,
  {
    focusX,
    focusY,
    zoom,
    mode = "cover",
    trimLeft = 0,
    trimRight = 0,
    trimTop = 0,
    trimBottom = 0,
  }
) => {
  const ctx = canvas.getContext("2d");
  if (!ctx || !image) return;

  const targetW = canvas.width;
  const targetH = canvas.height;
  const sourceW = image.naturalWidth || 1;
  const sourceH = image.naturalHeight || 1;
  const safeZoom = Math.max(1, zoom);
  const normalizedMode = String(mode || "cover").toLowerCase();

  let leftPct = clampRange(Number(trimLeft) || 0, 0, 45);
  let rightPct = clampRange(Number(trimRight) || 0, 0, 45);
  let topPct = clampRange(Number(trimTop) || 0, 0, 45);
  let bottomPct = clampRange(Number(trimBottom) || 0, 0, 45);

  // Keep a minimum drawable area even if both side trims are high.
  if (leftPct + rightPct > 90) {
    const scale = 90 / (leftPct + rightPct);
    leftPct *= scale;
    rightPct *= scale;
  }
  if (topPct + bottomPct > 90) {
    const scale = 90 / (topPct + bottomPct);
    topPct *= scale;
    bottomPct *= scale;
  }

  const trimmedLeft = sourceW * (leftPct / 100);
  const trimmedRight = sourceW * (rightPct / 100);
  const trimmedTop = sourceH * (topPct / 100);
  const trimmedBottom = sourceH * (bottomPct / 100);

  const availableW = Math.max(1, sourceW - trimmedLeft - trimmedRight);
  const availableH = Math.max(1, sourceH - trimmedTop - trimmedBottom);

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, targetW, targetH);

  if (normalizedMode === "contain") {
    const containScale = Math.min(targetW / availableW, targetH / availableH);
    const drawW = availableW * containScale;
    const drawH = availableH * containScale;
    const drawX = (targetW - drawW) / 2;
    const drawY = (targetH - drawH) / 2;
    ctx.drawImage(
      image,
      trimmedLeft,
      trimmedTop,
      availableW,
      availableH,
      drawX,
      drawY,
      drawW,
      drawH
    );
    return;
  }

  const targetRatio = targetW / targetH;
  const availableRatio = availableW / availableH;

  let baseCropW;
  let baseCropH;
  if (availableRatio > targetRatio) {
    baseCropH = availableH;
    baseCropW = baseCropH * targetRatio;
  } else {
    baseCropW = availableW;
    baseCropH = baseCropW / targetRatio;
  }

  const cropW = Math.max(1, baseCropW / safeZoom);
  const cropH = Math.max(1, baseCropH / safeZoom);
  const maxX = Math.max(0, availableW - cropW);
  const maxY = Math.max(0, availableH - cropH);
  const sourceX = trimmedLeft + maxX * clamp01(focusX);
  const sourceY = trimmedTop + maxY * clamp01(focusY);

  ctx.drawImage(image, sourceX, sourceY, cropW, cropH, 0, 0, targetW, targetH);
};

const getDefaultCropPreset = (sectionKey) => {
  const key = String(sectionKey || "").toLowerCase();
  if (key.includes("site_header_president")) return "1:1";
  if (key.includes("home_leadership_card_")) return "1:1";
  if (key.includes("home_champion_card_")) return "3:4";
  if (key.includes("home_initiative_card_")) return "16:9";
  if (key.includes("hero")) return "16:9";
  if (key.includes("home_dsc") || key.includes("home_pride") || key.includes("home_impact")) return "16:9";
  return "4:3";
};

const pageLabel = (value) => {
  const key = normalizePageKey(value);
  if (!key) return "-";
  if (key === "site_header") return "Site Header";
  if (key === "home") return "Home";
  if (key === "about_us") return "About Us";
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const normalizeImagePathsInput = (input) => {
  if (!input) return "";
  if (Array.isArray(input)) {
    return input
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .join("\n");
  }
  if (typeof input !== "string") return "";

  const text = input.trim();
  if (!text) return "";

  if (text.startsWith("[")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => String(item || "").trim())
          .filter(Boolean)
          .join("\n");
      }
    } catch (_err) {
      // Keep non-JSON text as-is.
    }
  }

  return text;
};

const getFileExtensionFromMime = (mime = "") => {
  const value = String(mime || "").toLowerCase();
  if (value.includes("png")) return "png";
  if (value.includes("webp")) return "webp";
  if (value.includes("gif")) return "gif";
  if (value.includes("bmp")) return "bmp";
  if (value.includes("avif")) return "avif";
  return "jpg";
};

const parseImagePathsArray = (input) =>
  normalizeImagePathsInput(input)
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeOrder = (list) =>
  list.map((item, index) => ({ ...item, sort_order: index + 1 }));

const ManageContentSections = () => {
  const [searchParams] = useSearchParams();
  const initialPage = useMemo(() => {
    const raw = normalizePageKey(searchParams.get("page") || "");
    return raw || "about_us";
  }, [searchParams]);

  const [selectedPage, setSelectedPage] = useState(initialPage);
  const [availablePages, setAvailablePages] = useState(() =>
    Array.from(new Set([...KNOWN_PAGE_KEYS, initialPage])).filter(Boolean)
  );
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...initialForm, page: initialPage });
  const [editingId, setEditingId] = useState(null);
  const [editingMainImagePath, setEditingMainImagePath] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [multiImageFiles, setMultiImageFiles] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2600 });
  }, [message]);

  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const [cropSourceUrl, setCropSourceUrl] = useState("");
  const [cropImage, setCropImage] = useState(null);
  const [cropPresetKey, setCropPresetKey] = useState("4:3");
  const [cropX, setCropX] = useState(50);
  const [cropY, setCropY] = useState(50);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropTrimLeft, setCropTrimLeft] = useState(0);
  const [cropTrimRight, setCropTrimRight] = useState(0);
  const [cropTrimTop, setCropTrimTop] = useState(0);
  const [cropTrimBottom, setCropTrimBottom] = useState(0);
  const [cropMode, setCropMode] = useState("cover");
  const [cropOutputScale, setCropOutputScale] = useState(100);
  const [cropQuality, setCropQuality] = useState(92);
  const [cropError, setCropError] = useState("");
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);
  const [isDraggingPreview, setIsDraggingPreview] = useState(false);
  const [isCropFrameDragging, setIsCropFrameDragging] = useState(false);
  const [cropFrameDrag, setCropFrameDrag] = useState(null);
  const [cropTarget, setCropTarget] = useState("main");
  const [cropQueue, setCropQueue] = useState([]);
  const [cropQueueIndex, setCropQueueIndex] = useState(0);
  const [preparedGalleryFiles, setPreparedGalleryFiles] = useState([]);
  const [multiImagePreviewUrls, setMultiImagePreviewUrls] = useState([]);
  const [isPreparingCurrentImageCrop, setIsPreparingCurrentImageCrop] = useState(false);

  const imageInputRef = useRef(null);
  const multiImageInputRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const cropFrameContainerRef = useRef(null);
  const previewDragRef = useRef(null);

  const activeCropPreset = CROP_PRESETS[cropPresetKey] || CROP_PRESETS["4:3"];
  const previewCanvasWidth = 640;
  const previewCanvasHeight = Math.round(
    (previewCanvasWidth * activeCropPreset.height) / activeCropPreset.width
  );
  const outputWidth = Math.max(
    320,
    Math.round((activeCropPreset.width * Number(cropOutputScale || 100)) / 100)
  );
  const outputHeight = Math.max(
    320,
    Math.round((activeCropPreset.height * Number(cropOutputScale || 100)) / 100)
  );
  const isCropOpen = Boolean(cropFile);
  const isGalleryCropSession = cropTarget === "gallery";
  const cropSessionTotal = isGalleryCropSession ? cropQueue.length || 1 : 1;
  const cropSessionIndex = isGalleryCropSession ? Math.min(cropQueueIndex + 1, cropSessionTotal) : 1;

  const loadSections = useCallback(
    async (page = selectedPage) => {
      const normalizedPage = normalizePageKey(page) || "about_us";
      setLoading(true);
      try {
        const response = await API.fetchAdminContentSections(normalizedPage);
        setItems(Array.isArray(response?.data) ? response.data : []);
        const pages = (Array.isArray(response?.pages) ? response.pages : [])
          .map((item) => normalizePageKey(item))
          .filter(Boolean);
        setAvailablePages((prev) =>
          Array.from(new Set([...KNOWN_PAGE_KEYS, ...prev, normalizedPage, ...pages]))
        );
      } catch (error) {
        console.error(error);
        setMessage("Failed to load sections.");
      } finally {
        setLoading(false);
      }
    },
    [selectedPage]
  );

  useEffect(() => {
    loadSections(selectedPage);
  }, [selectedPage, loadSections]);

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
      ),
    [items]
  );
  const selectedImagePaths = useMemo(
    () => parseImagePathsArray(form.image_paths),
    [form.image_paths]
  );
  const additionalPreviewImages = useMemo(() => {
    if (multiImagePreviewUrls.length) return multiImagePreviewUrls;
    return selectedImagePaths
      .map((path) =>
        resolveAssetUrl(path, {
          folder: "page-sections",
          defaultPath: "",
        })
      )
      .filter(Boolean);
  }, [multiImagePreviewUrls, selectedImagePaths]);
  const mainPreviewImage = useMemo(() => {
    if (imagePreviewUrl) return imagePreviewUrl;
    if (editingMainImagePath && !form.remove_image) {
      return resolveAssetUrl(editingMainImagePath, {
        folder: "page-sections",
        defaultPath: "",
      });
    }
    return "";
  }, [imagePreviewUrl, editingMainImagePath, form.remove_image]);
  const previewGalleryImages = useMemo(() => {
    const merged = [mainPreviewImage, ...additionalPreviewImages].filter(Boolean);
    return [...new Set(merged)];
  }, [mainPreviewImage, additionalPreviewImages]);
  const resolvedPreviewLayout = useMemo(
    () => resolveSectionGalleryLayout(form.image_layout, previewGalleryImages.length),
    [form.image_layout, previewGalleryImages.length]
  );
  const previewProfile = useMemo(
    () => getSectionPreviewProfile(form.section_key),
    [form.section_key]
  );
  const usesInitiativeCardPreview =
    previewProfile.id === "home_initiative_card" && resolvedPreviewLayout === "single";
  const previewVisibleCount = useMemo(
    () => getApproxVisibleCount(form.image_layout, previewGalleryImages.length),
    [form.image_layout, previewGalleryImages.length]
  );
  const layoutVisibilityHint = useMemo(
    () => getLayoutVisibilityHint(form.image_layout, previewGalleryImages.length),
    [form.image_layout, previewGalleryImages.length]
  );
  const previewFrameSizeClass = useMemo(
    () => getPreviewFrameSizeClass(form.image_size),
    [form.image_size]
  );
  const displayPreviewVisibleCount = usesInitiativeCardPreview
    ? Math.min(1, previewGalleryImages.length)
    : previewVisibleCount;
  const cropVisibleWidthPct = useMemo(
    () => Math.max(1, Math.round(100 - cropTrimLeft - cropTrimRight)),
    [cropTrimLeft, cropTrimRight]
  );
  const cropVisibleHeightPct = useMemo(
    () => Math.max(1, Math.round(100 - cropTrimTop - cropTrimBottom)),
    [cropTrimTop, cropTrimBottom]
  );
  const cropVisibleAreaPct = useMemo(
    () => Math.max(1, Math.round((cropVisibleWidthPct * cropVisibleHeightPct) / 100)),
    [cropVisibleWidthPct, cropVisibleHeightPct]
  );
  const cropFrameStyle = useMemo(
    () => ({
      left: `${cropTrimLeft}%`,
      top: `${cropTrimTop}%`,
      width: `${cropVisibleWidthPct}%`,
      height: `${cropVisibleHeightPct}%`,
    }),
    [cropTrimLeft, cropTrimTop, cropVisibleWidthPct, cropVisibleHeightPct]
  );

  useEffect(() => {
    if (!multiImageFiles.length) {
      setMultiImagePreviewUrls([]);
      return;
    }

    const urls = multiImageFiles.map((file) => URL.createObjectURL(file));
    setMultiImagePreviewUrls(urls);
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [multiImageFiles]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  useEffect(() => {
    if (!cropFile) {
      setCropSourceUrl("");
      setCropImage(null);
      return;
    }

    const url = URL.createObjectURL(cropFile);
    setCropSourceUrl(url);
    setCropImage(null);
    return () => URL.revokeObjectURL(url);
  }, [cropFile]);

  useEffect(() => {
    if (!cropSourceUrl) return;
    const image = new Image();
    image.onload = () => {
      setCropImage(image);
      setCropError("");
    };
    image.onerror = () => {
      setCropImage(null);
      setCropError("Could not load this image for crop preview.");
    };
    image.src = cropSourceUrl;
    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [cropSourceUrl]);

  useEffect(() => {
    if (!isCropOpen || !cropImage) return;
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    drawCropToCanvas(cropImage, canvas, {
      focusX: cropX / 100,
      focusY: cropY / 100,
      zoom: cropZoom,
      mode: cropMode,
      trimLeft: cropTrimLeft,
      trimRight: cropTrimRight,
      trimTop: cropTrimTop,
      trimBottom: cropTrimBottom,
    });
  }, [
    isCropOpen,
    cropImage,
    cropX,
    cropY,
    cropZoom,
    cropPresetKey,
    cropMode,
    cropTrimLeft,
    cropTrimRight,
    cropTrimTop,
    cropTrimBottom,
  ]);

  const closeCropModal = useCallback(() => {
    setCropFile(null);
    setCropSourceUrl("");
    setCropImage(null);
    setCropError("");
    setIsApplyingCrop(false);
    setIsDraggingPreview(false);
    setIsCropFrameDragging(false);
    setCropFrameDrag(null);
    setCropTrimLeft(0);
    setCropTrimRight(0);
    setCropTrimTop(0);
    setCropTrimBottom(0);
    setCropTarget("main");
    setCropQueue([]);
    setCropQueueIndex(0);
    setPreparedGalleryFiles([]);
    previewDragRef.current = null;
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === "remove_image" && checked) {
      setImageFile(null);
      if (imageInputRef.current) imageInputRef.current.value = "";
      closeCropModal();
    }

    if (name === "section_key") {
      const normalizedSectionKey = normalizeSectionKeyInput(value);
      const inferredPage = !editingId ? inferPageFromSectionKey(normalizedSectionKey) : "";
      if (inferredPage) {
        setSelectedPage(inferredPage);
        setAvailablePages((prev) =>
          Array.from(new Set([...KNOWN_PAGE_KEYS, ...prev, inferredPage]))
        );
      }
      setForm((prev) => ({
        ...prev,
        section_key: normalizedSectionKey,
        page: inferredPage || prev.page,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setEditingId(null);
    setEditingMainImagePath("");
    setImageFile(null);
    setMultiImageFiles([]);
    closeCropModal();
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (multiImageInputRef.current) multiImageInputRef.current.value = "";
    setForm({ ...initialForm, page: normalizePageKey(selectedPage) || "about_us" });
    setIsFormModalOpen(false);
  };

  const handleCloseModal = () => {
    resetForm();
    setIsFormModalOpen(false);
  };

  const populateForEdit = (item) => {
    setEditingId(item.id);
    setEditingMainImagePath(item.image_path || "");
    setImageFile(null);
    setMultiImageFiles([]);
    closeCropModal();
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (multiImageInputRef.current) multiImageInputRef.current.value = "";
    const normalizedItemPage = normalizePageKey(item.page) || selectedPage;
    setSelectedPage(normalizedItemPage);
    setAvailablePages((prev) =>
      Array.from(new Set([...KNOWN_PAGE_KEYS, ...prev, normalizedItemPage]))
    );
    setForm({
      page: normalizedItemPage,
      section_key: normalizeSectionKeyInput(item.section_key),
      heading: item.heading || "",
      subheading: item.subheading || "",
      body: item.body || "",
      image_paths: normalizeImagePathsInput(item.image_paths),
      image_layout: item.image_layout || "auto",
      image_fit: item.layout_config?.image_fit || "cover",
      image_size: item.layout_config?.image_size || "medium",
      layout: item.layout_config?.layout || "stack",
      media_position: item.layout_config?.media_position || "top",
      heading_align: item.layout_config?.heading_align || "left",
      text_align: item.layout_config?.text_align || "left",
      desktop_span: Number(item.layout_config?.desktop_span || 1),
      show_heading: item.layout_config?.show_heading !== false,
      show_subheading: item.layout_config?.show_subheading !== false,
      show_body: item.layout_config?.show_body !== false,
      show_image: item.layout_config?.show_image !== false,
      cta_label: item.cta_label || "",
      cta_link: item.cta_link || "",
      sort_order: item.sort_order ?? 0,
      is_active: Boolean(item.is_active),
      remove_image: false,
    });
    setIsFormModalOpen(true);
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      let valToSend = value;
      if (key === "section_key") {
        valToSend = String(value || "").replace(/^_+|_+$/g, "");
      }
      fd.append(
        key,
        key === "is_active" || key === "remove_image"
          ? value
            ? "1"
            : "0"
          : String(valToSend)
      );
    });
    if (imageFile) {
      fd.append("image", imageFile);
    }
    multiImageFiles.forEach((file) => {
      fd.append("images[]", file);
    });
    fd.append(
      "layout_config",
      JSON.stringify({
        layout: form.layout,
        media_position: form.media_position,
        show_heading: Boolean(form.show_heading),
        show_subheading: Boolean(form.show_subheading),
        show_body: Boolean(form.show_body),
        show_image: Boolean(form.show_image),
        heading_align: form.heading_align,
        text_align: form.text_align,
        desktop_span: Number(form.desktop_span || 1),
        image_fit: form.image_fit,
        image_size: form.image_size,
      })
    );
    return fd;
  };

  const resetCropFocus = () => {
    setCropX(50);
    setCropY(50);
    setCropZoom(1);
    setCropTrimLeft(0);
    setCropTrimRight(0);
    setCropTrimTop(0);
    setCropTrimBottom(0);
    setCropError("");
  };

  const openCropForFiles = (files, target = "main") => {
    const queue = (Array.isArray(files) ? files : []).filter(Boolean);
    if (!queue.length) return;

    setCropTarget(target === "gallery" ? "gallery" : "main");
    setCropQueue(queue);
    setCropQueueIndex(0);
    setPreparedGalleryFiles([]);
    setCropPresetKey(getDefaultCropPreset(form.section_key));
    setCropMode("cover");
    setCropOutputScale(100);
    setCropQuality(92);
    resetCropFocus();
    setCropFile(queue[0]);
  };

  const openCropForFile = (file, target = "main") => {
    openCropForFiles(file ? [file] : [], target);
  };

  const handleImageSelect = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = "";
    if (!selectedFile) return;
    setForm((prev) => ({ ...prev, remove_image: false }));
    openCropForFile(selectedFile);
  };

  const handleMultiImageSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) {
      setMultiImageFiles([]);
      return;
    }
    let filesForCrop = selectedFiles;
    if (selectedFiles.length > MAX_MULTI_IMAGE_UPLOAD) {
      filesForCrop = selectedFiles.slice(0, MAX_MULTI_IMAGE_UPLOAD);
      setMessage(
        `Selected ${selectedFiles.length} images. Only first ${MAX_MULTI_IMAGE_UPLOAD} will be uploaded in one request.`
      );
    } else {
      setMessage(
        `Crop each additional image (${selectedFiles.length} total), then they will be prepared for upload.`
      );
    }
    setMultiImageFiles([]);
    openCropForFiles(filesForCrop, "gallery");
  };

  const handleRemoveSelectedImagePath = async (pathToRemove) => {
    const result = await confirmAlert({
      title: "Remove this photo?",
      text: "It will be removed when you click Update Section.",
      confirmText: "Remove",
    });
    if (!result.isConfirmed) return;

    setForm((prev) => {
      const next = parseImagePathsArray(prev.image_paths).filter(
        (path) => path !== pathToRemove
      );
      return { ...prev, image_paths: next.join("\n") };
    });
  };

  const handleRemoveMainImage = async () => {
    const result = await confirmAlert({
      title: "Remove main image?",
      text: "It will be removed when you click Update Section.",
      confirmText: "Remove",
    });
    if (!result.isConfirmed) return;

    setForm((prev) => ({ ...prev, remove_image: true }));
    setImageFile(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
    closeCropModal();
  };

  const handleAdjustCurrentImage = async () => {
    if (!editingMainImagePath) return;

    setIsPreparingCurrentImageCrop(true);
    try {
      const sourceUrl = resolveAssetUrl(editingMainImagePath, {
        folder: "page-sections",
        defaultPath: "",
      });
      if (!sourceUrl) {
        setMessage("Failed to load current image for adjustment.");
        return;
      }

      const response = await fetch(sourceUrl, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`Image fetch failed with ${response.status}`);
      }

      const blob = await response.blob();
      const ext = getFileExtensionFromMime(blob.type);
      const fileName = `${form.section_key || "section-main"}-adjusted-${Date.now()}.${ext}`;
      const file = new File([blob], fileName, {
        type: blob.type || "image/jpeg",
        lastModified: Date.now(),
      });

      setForm((prev) => ({ ...prev, remove_image: false }));
      openCropForFile(file);
      setMessage("Adjust the current image crop and save.");
    } catch (error) {
      console.error(error);
      setMessage("Failed to load current image for adjustment.");
    } finally {
      setIsPreparingCurrentImageCrop(false);
    }
  };

  const applyPreparedMainImage = (preparedFile) => {
    setImageFile(preparedFile);
    setForm((prev) => ({ ...prev, remove_image: false }));
    setMessage("Image prepared and ready to upload.");
    closeCropModal();
  };

  const applyPreparedGalleryImage = (preparedFile) => {
    const nextPrepared = [...preparedGalleryFiles, preparedFile];
    const nextIndex = cropQueueIndex + 1;

    if (nextIndex < cropQueue.length) {
      setPreparedGalleryFiles(nextPrepared);
      setCropQueueIndex(nextIndex);
      resetCropFocus();
      setCropFile(cropQueue[nextIndex]);
      return;
    }

    setMultiImageFiles(nextPrepared);
    setMessage(`${nextPrepared.length} additional image(s) prepared and ready to upload.`);
    closeCropModal();
  };

  const handleUseOriginalImage = () => {
    if (!cropFile) return;
    if (cropTarget === "gallery") {
      applyPreparedGalleryImage(cropFile);
      return;
    }
    applyPreparedMainImage(cropFile);
  };

  const handleApplyCrop = async () => {
    if (!cropFile || !cropImage) return;
    setIsApplyingCrop(true);
    setCropError("");

    try {
      const canvas = document.createElement("canvas");
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      drawCropToCanvas(cropImage, canvas, {
        focusX: cropX / 100,
        focusY: cropY / 100,
        zoom: cropZoom,
        mode: cropMode,
        trimLeft: cropTrimLeft,
        trimRight: cropTrimRight,
        trimTop: cropTrimTop,
        trimBottom: cropTrimBottom,
      });

      const mimeType = cropFile.type === "image/png" ? "image/png" : "image/jpeg";
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) resolve(result);
            else reject(new Error("Canvas export failed."));
          },
          mimeType,
          mimeType === "image/jpeg" ? Number(cropQuality || 92) / 100 : undefined
        );
      });

      const croppedFile = new File([blob], cropFile.name, {
        type: mimeType,
        lastModified: Date.now(),
      });
      if (cropTarget === "gallery") {
        applyPreparedGalleryImage(croppedFile);
      } else {
        applyPreparedMainImage(croppedFile);
      }
    } catch (error) {
      console.error(error);
      setCropError("Failed to crop this image. Try again or use original.");
    } finally {
      setIsApplyingCrop(false);
    }
  };

  const handleCancelCrop = () => {
    const isGallery = cropTarget === "gallery";
    closeCropModal();
    if (isGallery) {
      if (multiImageInputRef.current) multiImageInputRef.current.value = "";
      setMessage("Additional image crop cancelled.");
      return;
    }
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const startCropFrameDrag = (event, handle) => {
    if (!cropImage || !isCropOpen) return;
    const rect = cropFrameContainerRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;

    event.preventDefault();
    event.stopPropagation();
    previewDragRef.current = null;
    setIsDraggingPreview(false);
    setIsCropFrameDragging(true);
    setCropFrameDrag({
      handle,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      containerWidth: rect.width,
      containerHeight: rect.height,
      startLeft: cropTrimLeft,
      startRight: cropTrimRight,
      startTop: cropTrimTop,
      startBottom: cropTrimBottom,
    });
  };

  useEffect(() => {
    if (!cropFrameDrag) return;

    const onPointerMove = (event) => {
      if (event.pointerId !== cropFrameDrag.pointerId) return;

      const dxPct = ((event.clientX - cropFrameDrag.startX) / cropFrameDrag.containerWidth) * 100;
      const dyPct = ((event.clientY - cropFrameDrag.startY) / cropFrameDrag.containerHeight) * 100;

      let nextLeft = cropFrameDrag.startLeft;
      let nextRight = cropFrameDrag.startRight;
      let nextTop = cropFrameDrag.startTop;
      let nextBottom = cropFrameDrag.startBottom;

      if (cropFrameDrag.handle === "move") {
        const width = 100 - cropFrameDrag.startLeft - cropFrameDrag.startRight;
        const height = 100 - cropFrameDrag.startTop - cropFrameDrag.startBottom;
        const minLeft = Math.max(0, 100 - width - 45);
        const maxLeft = Math.min(45, 100 - width);
        const minTop = Math.max(0, 100 - height - 45);
        const maxTop = Math.min(45, 100 - height);

        nextLeft = clampRange(cropFrameDrag.startLeft + dxPct, minLeft, maxLeft);
        nextRight = 100 - width - nextLeft;
        nextTop = clampRange(cropFrameDrag.startTop + dyPct, minTop, maxTop);
        nextBottom = 100 - height - nextTop;
      } else {
        if (cropFrameDrag.handle.includes("left")) {
          nextLeft = clampRange(
            cropFrameDrag.startLeft + dxPct,
            0,
            Math.min(45, 90 - cropFrameDrag.startRight)
          );
        }
        if (cropFrameDrag.handle.includes("right")) {
          nextRight = clampRange(
            cropFrameDrag.startRight - dxPct,
            0,
            Math.min(45, 90 - cropFrameDrag.startLeft)
          );
        }
        if (cropFrameDrag.handle.includes("top")) {
          nextTop = clampRange(
            cropFrameDrag.startTop + dyPct,
            0,
            Math.min(45, 90 - cropFrameDrag.startBottom)
          );
        }
        if (cropFrameDrag.handle.includes("bottom")) {
          nextBottom = clampRange(
            cropFrameDrag.startBottom - dyPct,
            0,
            Math.min(45, 90 - cropFrameDrag.startTop)
          );
        }
      }

      setCropTrimLeft(Math.round(nextLeft));
      setCropTrimRight(Math.round(nextRight));
      setCropTrimTop(Math.round(nextTop));
      setCropTrimBottom(Math.round(nextBottom));
    };

    const onPointerEnd = (event) => {
      if (event.pointerId !== cropFrameDrag.pointerId) return;
      setCropFrameDrag(null);
      setIsCropFrameDragging(false);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerEnd);
    window.addEventListener("pointercancel", onPointerEnd);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerEnd);
      window.removeEventListener("pointercancel", onPointerEnd);
    };
  }, [cropFrameDrag]);

  const handlePreviewPointerDown = (event) => {
    if (!cropImage) return;
    if (cropFrameDrag) return;
    event.preventDefault();
    previewDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startCropX: cropX,
      startCropY: cropY,
    };
    setIsDraggingPreview(true);
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handlePreviewPointerMove = (event) => {
    const state = previewDragRef.current;
    if (!state || state.pointerId !== event.pointerId) return;

    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const deltaX = event.clientX - state.startX;
    const deltaY = event.clientY - state.startY;
    const nextX = state.startCropX - (deltaX / rect.width) * 100;
    const nextY = state.startCropY - (deltaY / rect.height) * 100;

    setCropX(clampRange(nextX, 0, 100));
    setCropY(clampRange(nextY, 0, 100));
  };

  const handlePreviewPointerUp = (event) => {
    const state = previewDragRef.current;
    if (!state || state.pointerId !== event.pointerId) return;
    previewDragRef.current = null;
    setIsDraggingPreview(false);
    if (event.currentTarget.releasePointerCapture) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handlePreviewWheel = (event) => {
    event.preventDefault();
    const step = event.shiftKey ? 0.2 : 0.08;
    setCropZoom((prev) => {
      const next = prev + (event.deltaY < 0 ? step : -step);
      return clampRange(next, 1, 3);
    });
  };

  const handleHorizontalTrimChange = (side, nextValue) => {
    const safeValue = clampRange(Number(nextValue) || 0, 0, 45);
    if (side === "left") {
      const allowed = Math.max(0, 90 - cropTrimRight);
      setCropTrimLeft(clampRange(safeValue, 0, allowed));
      return;
    }
    const allowed = Math.max(0, 90 - cropTrimLeft);
    setCropTrimRight(clampRange(safeValue, 0, allowed));
  };

  const handleVerticalTrimChange = (side, nextValue) => {
    const safeValue = clampRange(Number(nextValue) || 0, 0, 45);
    if (side === "top") {
      const allowed = Math.max(0, 90 - cropTrimBottom);
      setCropTrimTop(clampRange(safeValue, 0, allowed));
      return;
    }
    const allowed = Math.max(0, 90 - cropTrimTop);
    setCropTrimBottom(clampRange(safeValue, 0, allowed));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    try {
      const fd = buildFormData();
      let response;
      if (editingId) {
        response = await API.updateContentSection(editingId, fd);
        setMessage(response?.message || "Section updated successfully.");
      } else {
        response = await API.createContentSection(fd);
        setMessage(response?.message || "Section created successfully.");
      }
      await loadSections(selectedPage);
      resetForm();
    } catch (error) {
      console.error(error);
      const firstError = error?.errors
        ? Object.values(error.errors).flat().find(Boolean)
        : "";
      setMessage(firstError || error?.message || "Failed to save section.");
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmAlert({
      title: "Delete this section?",
      text: "This action cannot be undone",
      confirmText: "Delete",
    });
    if (!result.isConfirmed) return;

    try {
      await API.deleteContentSection(id);
      setMessage("Section deleted.");
      await loadSections(selectedPage);
      if (editingId === id) resetForm();
    } catch (error) {
      console.error(error);
      setMessage("Failed to delete section.");
    }
  };

  const handleRowDrop = async (targetId) => {
    if (!draggingId || draggingId === targetId || isReordering) return;

    const fromIndex = sortedItems.findIndex((item) => item.id === draggingId);
    const toIndex = sortedItems.findIndex((item) => item.id === targetId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggingId(null);
      return;
    }

    const next = [...sortedItems];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    const nextWithOrder = normalizeOrder(next);

    setItems(nextWithOrder);
    setDraggingId(null);
    setIsReordering(true);
    setMessage("Saving new section order...");

    try {
      await API.reorderContentSections(
        nextWithOrder.map((item, index) => ({
          id: item.id,
          sort_order: index + 1,
        })),
        selectedPage
      );
      setMessage("Section order updated.");
      await loadSections(selectedPage);
    } catch (error) {
      console.error(error);
      setMessage("Failed to update section order.");
      await loadSections(selectedPage);
    } finally {
      setIsReordering(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <h1 className="text-2xl md:text-3xl font-bold text-blue-700 mb-4">
          Manage Website Sections
        </h1>
        {message ? (
          <div className="mb-4 rounded bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2">
            {message}
          </div>
        ) : null}
        <div className="mb-4 rounded bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm">
          <p className="font-semibold">
            Quick guide: choose page, enter section key, add content, then save.
          </p>
          <p className="mt-1">
            This screen supports every page key and every section key.
          </p>
          <details className="mt-2">
            <summary className="cursor-pointer font-semibold text-amber-900">
              Show key examples and formatting rules
            </summary>
            <div className="mt-2 space-y-2 text-xs leading-5">
              <p>
                General pages: <strong>`events`, `gallery`, `players`, `news`, `donate`, `messages`</strong>.
              </p>
              <p>
                Site header: <strong>`site_header_president`</strong> for editable president name, title lines, and photo in the public header.
              </p>
              <p>
                Home/About core keys: <strong>`home_hero`, `home_impact`, `home_dsc`, `home_policy`, `home_about`, `home_pride`, `home_timeline`, `home_leadership`, `home_champions`, `home_initiatives`, `home_vision`, `home_media`, `home_cta`, `about_hero`</strong>.
              </p>
              <p>
                Repeatable cards: <strong>`why_join_card_*`, `home_timeline_item_*`, `home_leadership_card_*`, `home_champion_card_*`, `home_initiative_card_*`, `home_vision_point_*`, `home_impact_card_*`</strong>.
              </p>
              <p>
                Subsections: <strong>`home_dsc_subsection_*`, `home_policy_subsection_*`, `home_pride_subsection_*`, `home_timeline_subsection_*`, `home_leadership_subsection_*`, `home_champions_subsection_*`, `home_initiatives_subsection_*`, `home_vision_subsection_*`, `home_media_subsection_*`, `home_cta_subsection_*`</strong>.
              </p>
              <p>
                Donate: <strong>`donate_hero`, `donate_why_meta`, `donate_why_card_*`, `donate_ways_meta`, `donate_bank`, `donate_upi`</strong>.
              </p>
              <p>
                `donate_bank` body format: <strong>`Account Name: ...`, `Account Number: ...`, `IFSC Code: ...`, `Bank Name: ...`, `Note: ...`</strong> (single line with commas or one key per line).
              </p>
              <p>
                `donate_upi`: heading = title, subheading = instruction, body = UPI ID, image = QR.
              </p>
              <p>
                Messages: <strong>`messages_hero`, `messages_leader_*`</strong>. Leader item: heading = name, subheading = role, body = message, image = photo.
              </p>
              <p>
                About affiliated: <strong>`about_affiliated_meta`, `about_affiliated_body_*`</strong>.
              </p>
              <p>
                Updates & Results: <strong>`updates_results_meta`, `updates_live_meta`, `updates_result_sheet_*`</strong>. For result sheets use CTA label + CTA link for download/open.
              </p>
            </div>
          </details>
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">
                Page key (manual)
              </label>
              <input
                value={selectedPage}
                onChange={(e) => {
                  const page = normalizePageKey(e.target.value);
                  const normalized = page || "about_us";
                  setSelectedPage(normalized);
                  setAvailablePages((prev) =>
                    Array.from(new Set([...KNOWN_PAGE_KEYS, ...prev, normalized]))
                  );
                  setForm((prev) => ({ ...prev, page: normalized }));
                  setEditingId(null);
                }}
                placeholder="e.g. home, about_us, events"
                className="border rounded px-3 py-2 w-full text-sm"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">
                Page quick select
              </label>
              <select
                value={selectedPage}
                onChange={(e) => {
                  const page = normalizePageKey(e.target.value) || "about_us";
                  setSelectedPage(page);
                  setForm((prev) => ({ ...prev, page }));
                  setEditingId(null);
                }}
                className="border rounded px-3 py-2 w-full text-sm"
              >
                {Array.from(new Set([...KNOWN_PAGE_KEYS, ...availablePages, selectedPage]))
                  .filter(Boolean)
                  .sort()
                  .map((p) => (
                    <option key={p} value={p}>
                      {pageLabel(p)}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setEditingId(null);
                  setIsFormModalOpen(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-sm w-full h-[40px]"
              >
                <span>➕</span> Create New Section
              </button>
            </div>
          </div>
        </div>

        <Modal
          isOpen={isFormModalOpen}
          onClose={handleCloseModal}
          title={editingId ? "Edit Website Section" : "Create New Website Section"}
          width="1000px"
        >
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-800">1. Basic section details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Selected page</label>
                  <input
                    name="page"
                    value={form.page}
                    readOnly
                    className="border rounded px-3 py-2 bg-slate-100 text-slate-600 w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Section key</label>
                  <input
                    name="section_key"
                    value={form.section_key}
                    onChange={handleChange}
                    placeholder="e.g. home_hero"
                    className="border rounded px-3 py-2 w-full"
                    list="section-key-suggestions"
                    required
                  />
                  <datalist id="section-key-suggestions">
                    {SECTION_KEY_SUGGESTIONS.map((key) => (
                      <option key={key} value={key} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Heading</label>
                  <input
                    name="heading"
                    value={form.heading}
                    onChange={handleChange}
                    placeholder="Heading"
                    className="border rounded px-3 py-2 w-full"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Subheading</label>
                  <input
                    name="subheading"
                    value={form.subheading}
                    onChange={handleChange}
                    placeholder="Subheading (optional)"
                    className="border rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Sort order</label>
                  <input
                    name="sort_order"
                    type="number"
                    value={form.sort_order}
                    onChange={handleChange}
                    placeholder="Sort order"
                    className="border rounded px-3 py-2 w-full"
                    min="0"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Body / content</label>
                  <textarea
                    name="body"
                    value={form.body}
                    onChange={handleChange}
                    placeholder="Section body/content"
                    rows={6}
                    className="border rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">CTA label</label>
                  <input
                    name="cta_label"
                    value={form.cta_label}
                    onChange={handleChange}
                    placeholder="CTA label (optional)"
                    className="border rounded px-3 py-2 w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">CTA link</label>
                  <input
                    name="cta_link"
                    value={form.cta_link}
                    onChange={handleChange}
                    placeholder="CTA link (optional)"
                    className="border rounded px-3 py-2 w-full"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-6 border-t border-slate-100 pt-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                  />
                  Active
                </label>
                {editingId ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      name="remove_image"
                      checked={form.remove_image}
                      onChange={handleChange}
                    />
                    Remove main image
                  </label>
                ) : null}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-800">2. Images</p>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Main image</label>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onClick={(e) => {
                    e.currentTarget.value = "";
                  }}
                  onChange={handleImageSelect}
                  className="border rounded px-3 py-2 w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Selecting a main image opens crop preview.
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Additional photos</label>
                <input
                  ref={multiImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleMultiImageSelect}
                  className="border rounded px-3 py-2 w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Multi-select opens crop for each image.
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">
                  Additional image URLs/paths (optional)
                </label>
                <textarea
                  name="image_paths"
                  value={form.image_paths}
                  onChange={handleChange}
                  placeholder={"One path per line.\n/uploads/page-sections/photo-1.jpg\n/uploads/page-sections/photo-2.jpg"}
                  rows={4}
                  className="border rounded px-3 py-2 w-full"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Use this only when you want to paste paths manually.
                </p>
              </div>
            </div>
          </div>

          <details className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <summary className="cursor-pointer text-sm font-semibold text-slate-800">
              3. Advanced display settings
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <select
                name="image_layout"
                value={form.image_layout}
                onChange={handleChange}
                className="border rounded px-3 py-2"
              >
                <option value="auto">Image layout: Auto</option>
                <option value="slider">Image layout: Slider + thumbnails</option>
                <option value="horizontal">Image layout: Horizontal scroll</option>
                <option value="vertical">Image layout: Vertical stack</option>
                <option value="grid2">Image layout: Grid (2 columns)</option>
                <option value="grid3">Image layout: Grid (3 columns)</option>
              </select>
              <select
                name="layout"
                value={form.layout}
                onChange={handleChange}
                className="border rounded px-3 py-2"
              >
                <option value="stack">Section layout: Stack</option>
                <option value="split">Section layout: Split</option>
              </select>
              <select
                name="image_fit"
                value={form.image_fit}
                onChange={handleChange}
                className="border rounded px-3 py-2"
              >
                <option value="cover">Image fit: Cover (crop to fill)</option>
                <option value="contain">Image fit: Contain (show full image)</option>
                <option value="contain_width">Image fit: Match width</option>
                <option value="contain_height">Image fit: Match height</option>
              </select>
              <select
                name="image_size"
                value={form.image_size}
                onChange={handleChange}
                className="border rounded px-3 py-2"
              >
                <option value="small">Image size: Small</option>
                <option value="medium">Image size: Medium</option>
                <option value="large">Image size: Large</option>
                <option value="xlarge">Image size: XL</option>
              </select>
              <select
                name="media_position"
                value={form.media_position}
                onChange={handleChange}
                className="border rounded px-3 py-2"
              >
                <option value="top">Media position: Top</option>
                <option value="bottom">Media position: Bottom</option>
                <option value="left">Media position: Left</option>
                <option value="right">Media position: Right</option>
              </select>
              <select
                name="desktop_span"
                value={form.desktop_span}
                onChange={handleChange}
                className="border rounded px-3 py-2"
              >
                <option value={1}>Width on desktop: Half</option>
                <option value={2}>Width on desktop: Full</option>
              </select>
              <select
                name="heading_align"
                value={form.heading_align}
                onChange={handleChange}
                className="border rounded px-3 py-2"
              >
                <option value="left">Heading align: Left</option>
                <option value="center">Heading align: Center</option>
                <option value="right">Heading align: Right</option>
              </select>
              <select
                name="text_align"
                value={form.text_align}
                onChange={handleChange}
                className="border rounded px-3 py-2"
              >
                <option value="left">Text align: Left</option>
                <option value="center">Text align: Center</option>
                <option value="right">Text align: Right</option>
              </select>
            </div>
            <div className="mt-3 flex flex-wrap gap-5 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="show_image"
                  checked={form.show_image}
                  onChange={handleChange}
                />
                Show image
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="show_heading"
                  checked={form.show_heading}
                  onChange={handleChange}
                />
                Show heading
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="show_subheading"
                  checked={form.show_subheading}
                  onChange={handleChange}
                />
                Show subheading
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="show_body"
                  checked={form.show_body}
                  onChange={handleChange}
                />
                Show body
              </label>
            </div>
          </details>

          {imagePreviewUrl ? (
            <div className="mt-4 rounded border border-slate-200 p-3 max-w-md">
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Prepared image preview
              </p>
              <img
                src={imagePreviewUrl}
                alt="Prepared upload"
                className="w-full h-auto rounded border border-slate-200"
              />
            </div>
          ) : null}

          {editingId && editingMainImagePath && !imagePreviewUrl ? (
            <div className="mt-3 rounded border border-slate-200 p-3 max-w-md">
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Current main image
              </p>
              <img
                src={resolveAssetUrl(editingMainImagePath, {
                  folder: "page-sections",
                  defaultPath: "/images/image-fallback.svg",
                })}
                alt="Current main section"
                className={`w-full h-auto rounded border ${
                  form.remove_image ? "opacity-40" : "opacity-100"
                }`}
              />
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={handleAdjustCurrentImage}
                  disabled={isPreparingCurrentImageCrop}
                  className="px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded disabled:opacity-60"
                >
                  {isPreparingCurrentImageCrop ? "Preparing..." : "Adjust current image"}
                </button>
                <button
                  type="button"
                  onClick={handleRemoveMainImage}
                  className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded"
                >
                  Remove main image
                </button>
                {form.remove_image ? (
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, remove_image: false }))}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded"
                  >
                    Undo remove
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {multiImageFiles.length ? (
            <div className="mt-3 rounded border border-slate-200 p-3">
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Additional images ready for upload ({multiImageFiles.length})
              </p>
              <div className="text-xs text-slate-600 space-y-1">
                {multiImageFiles.map((file) => (
                  <div key={`${file.name}-${file.size}-${file.lastModified}`}>
                    {file.name}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-semibold text-slate-800">
              Section image display preview
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Layout mode: <strong>{resolvedPreviewLayout}</strong>. Fit:{" "}
              <strong>{form.image_fit}</strong>. Size: <strong>{form.image_size}</strong>.{" "}
              {layoutVisibilityHint}
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Render profile: <strong>{previewProfile.label}</strong>. {previewProfile.note}
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Total images: <strong>{previewGalleryImages.length}</strong>. Approx visible at once:{" "}
              <strong>{displayPreviewVisibleCount}</strong>.
            </p>
            {!previewGalleryImages.length ? (
              <div className="mt-3 rounded border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
                Select/upload images to preview how many images will be visible.
              </div>
            ) : (
              <div className="mt-3 rounded border border-slate-200 bg-white p-3">
                {usesInitiativeCardPreview ? (
                  <div className="max-w-3xl mx-auto">
                    <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-100 bg-white">
                      <div className="overflow-hidden bg-slate-100" style={{ aspectRatio: "16 / 9" }}>
                        <img
                          src={previewGalleryImages[0]}
                          alt="Home initiative card preview"
                          className={`w-full h-full ${getPreviewImageClass(form.image_fit)}`}
                        />
                      </div>
                      <div className="p-5">
                        <p className="text-sm font-semibold text-slate-900">
                          Home initiative card image frame (`16:9`)
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
                {resolvedPreviewLayout === "single" || resolvedPreviewLayout === "slider" ? (
                  <>
                    <div
                      className={`rounded-lg overflow-hidden bg-slate-100 ${
                        usesInitiativeCardPreview
                          ? "hidden"
                          : `w-full aspect-[16/9] ${previewFrameSizeClass} mx-auto`
                      }`}
                    >
                      <img
                        src={previewGalleryImages[0]}
                        alt="Section main preview"
                        className={`w-full h-full ${getPreviewImageClass(form.image_fit)}`}
                      />
                    </div>
                    {previewGalleryImages.length > 1 && !usesInitiativeCardPreview ? (
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-2">
                        {previewGalleryImages.slice(0, 10).map((src, idx) => (
                          <div key={`${src}-${idx}`} className="h-14 rounded overflow-hidden bg-slate-100">
                            <img
                              src={src}
                              alt={`Section preview ${idx + 1}`}
                              className={`w-full h-full ${getPreviewImageClass(form.image_fit)}`}
                            />
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}

                {resolvedPreviewLayout === "horizontal" ? (
                  <div className="flex gap-2 overflow-x-auto">
                    {previewGalleryImages.slice(0, 6).map((src, idx) => (
                      <div key={`${src}-${idx}`} className="h-24 w-36 shrink-0 rounded overflow-hidden bg-slate-100">
                        <img
                          src={src}
                          alt={`Horizontal preview ${idx + 1}`}
                          className={`w-full h-full ${getPreviewImageClass(form.image_fit)}`}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}

                {resolvedPreviewLayout === "vertical" ? (
                  <div className="space-y-2">
                    {previewGalleryImages.slice(0, 4).map((src, idx) => (
                      <div key={`${src}-${idx}`} className="h-24 w-full rounded overflow-hidden bg-slate-100">
                        <img
                          src={src}
                          alt={`Vertical preview ${idx + 1}`}
                          className={`w-full h-full ${getPreviewImageClass(form.image_fit)}`}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}

                {resolvedPreviewLayout === "grid2" || resolvedPreviewLayout === "grid3" ? (
                  <div
                    className={`grid gap-2 ${
                      resolvedPreviewLayout === "grid3" ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2"
                    }`}
                  >
                    {previewGalleryImages.slice(0, 9).map((src, idx) => (
                      <div key={`${src}-${idx}`} className="h-20 rounded overflow-hidden bg-slate-100">
                        <img
                          src={src}
                          alt={`Grid preview ${idx + 1}`}
                          className={`w-full h-full ${getPreviewImageClass(form.image_fit)}`}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {editingId && selectedImagePaths.length ? (
            <div className="mt-3 rounded border border-slate-200 p-3">
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Existing additional photos (click remove, then update)
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {selectedImagePaths.map((path) => (
                  <div key={path} className="border rounded-lg overflow-hidden bg-white">
                    <img
                      src={resolveAssetUrl(path, {
                        folder: "page-sections",
                        defaultPath: "/images/image-fallback.svg",
                      })}
                      alt="Section additional"
                      className="w-full h-24 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveSelectedImagePath(path)}
                      className="w-full text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 py-1"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

            <div className="mt-5 pt-4 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCloseModal}
                className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold px-4 py-2 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg text-sm shadow-sm"
              >
                {editingId ? "Update Section" : "Add Section"}
              </button>
            </div>
          </form>
        </Modal>

        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <div className="px-3 pt-3 text-xs text-slate-500">
            Drag rows to reorder sections.
          </div>
          <table className="min-w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Page</th>
                <th className="text-left px-3 py-2">Image</th>
                <th className="text-left px-3 py-2">Heading</th>
                <th className="text-left px-3 py-2">Key</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-slate-500">
                    Loading...
                  </td>
                </tr>
              ) : sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-slate-500">
                    No sections found.
                  </td>
                </tr>
              ) : (
                sortedItems.map((item) => (
                  <tr
                    key={item.id}
                    className={`border-t ${draggingId === item.id ? "opacity-60 bg-blue-50" : ""}`}
                    draggable={!isReordering}
                    onDragStart={() => setDraggingId(item.id)}
                    onDragEnd={() => setDraggingId(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleRowDrop(item.id)}
                  >
                    <td className="px-3 py-2 cursor-move">
                      <span className="mr-2 text-slate-400">::</span>
                      {item.sort_order ?? 0}
                    </td>
                    <td className="px-3 py-2">{pageLabel(item.page)}</td>
                    <td className="px-3 py-2">
                      <img
                        src={resolveAssetUrl(item.image_path || "", {
                          folder: "page-sections",
                          defaultPath: "/images/image-fallback.svg",
                        })}
                        alt={item.heading || "section"}
                        className="w-28 h-16 object-cover rounded border"
                      />
                    </td>
                    <td className="px-3 py-2">{item.heading}</td>
                    <td className="px-3 py-2">{item.section_key}</td>
                    <td className="px-3 py-2">
                      {item.is_active ? "Active" : "Inactive"}
                    </td>
                    <td className="px-3 py-2 space-x-2">
                      <button
                        onClick={() => populateForEdit(item)}
                        className="px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isCropOpen ? (
        <div className="fixed inset-0 z-[10001] bg-black/70 p-4 flex items-center justify-center">
          <div className="w-full max-w-3xl bg-white rounded-xl shadow-xl max-h-[92vh] flex flex-col overflow-hidden">
            <div className="px-5 pt-5 pb-3 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">
                {isGalleryCropSession
                  ? `Crop Additional Image (${cropSessionIndex}/${cropSessionTotal})`
                  : "Crop Image"}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {cropFile?.name || "Selected image"}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Drag crop frame edges/corners directly on image. Drag inside frame to move it. Scroll to zoom and use focus sliders if needed.
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Visible source after side-crop: width <strong>{cropVisibleWidthPct}%</strong>, height{" "}
                <strong>{cropVisibleHeightPct}%</strong>, area <strong>{cropVisibleAreaPct}%</strong>.
              </p>
            </div>

            <div className="px-5 py-4 overflow-y-auto">
              <div className="p-3 border rounded-lg bg-slate-50 flex items-center justify-center touch-none">
                <div
                  ref={cropFrameContainerRef}
                  className={`relative w-full max-w-[640px] rounded border border-slate-200 bg-slate-200 overflow-hidden ${
                    isDraggingPreview || isCropFrameDragging ? "cursor-grabbing" : "cursor-grab"
                  }`}
                  onWheel={handlePreviewWheel}
                  onPointerDown={handlePreviewPointerDown}
                  onPointerMove={handlePreviewPointerMove}
                  onPointerUp={handlePreviewPointerUp}
                  onPointerCancel={handlePreviewPointerUp}
                >
                  <canvas
                    ref={previewCanvasRef}
                    width={previewCanvasWidth}
                    height={previewCanvasHeight}
                    className="w-full h-auto block select-none"
                  />
                  {cropImage ? (
                    <div className="pointer-events-none absolute inset-0">
                      <div
                        className="pointer-events-auto absolute border-2 border-blue-500"
                        style={{
                          ...cropFrameStyle,
                          boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.35)",
                        }}
                      >
                        <div
                          className="absolute inset-2 cursor-move"
                          onPointerDown={(event) => startCropFrameDrag(event, "move")}
                        />
                        <div
                          className="absolute -left-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white cursor-ew-resize"
                          onPointerDown={(event) => startCropFrameDrag(event, "left")}
                        />
                        <div
                          className="absolute -right-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white cursor-ew-resize"
                          onPointerDown={(event) => startCropFrameDrag(event, "right")}
                        />
                        <div
                          className="absolute left-1/2 -top-2 -translate-x-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white cursor-ns-resize"
                          onPointerDown={(event) => startCropFrameDrag(event, "top")}
                        />
                        <div
                          className="absolute left-1/2 -bottom-2 -translate-x-1/2 h-4 w-4 rounded-full border-2 border-blue-600 bg-white cursor-ns-resize"
                          onPointerDown={(event) => startCropFrameDrag(event, "bottom")}
                        />
                        <div
                          className="absolute -left-2 -top-2 h-4 w-4 rounded-full border-2 border-blue-700 bg-white cursor-nwse-resize"
                          onPointerDown={(event) => startCropFrameDrag(event, "top-left")}
                        />
                        <div
                          className="absolute -right-2 -top-2 h-4 w-4 rounded-full border-2 border-blue-700 bg-white cursor-nesw-resize"
                          onPointerDown={(event) => startCropFrameDrag(event, "top-right")}
                        />
                        <div
                          className="absolute -left-2 -bottom-2 h-4 w-4 rounded-full border-2 border-blue-700 bg-white cursor-nesw-resize"
                          onPointerDown={(event) => startCropFrameDrag(event, "bottom-left")}
                        />
                        <div
                          className="absolute -right-2 -bottom-2 h-4 w-4 rounded-full border-2 border-blue-700 bg-white cursor-nwse-resize"
                          onPointerDown={(event) => startCropFrameDrag(event, "bottom-right")}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {!cropImage && cropSourceUrl && !cropError ? (
                <div className="mt-3 rounded border border-slate-200 bg-white p-2">
                  <p className="text-xs text-slate-500 mb-2">Loading crop preview...</p>
                  <img
                    src={cropSourceUrl}
                    alt="Selected for crop"
                    className="max-h-40 w-auto mx-auto rounded border border-slate-200"
                  />
                </div>
              ) : null}

              {cropError ? (
                <p className="text-sm text-red-600 mt-3">{cropError}</p>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                <label className="text-sm text-slate-700">
                  Aspect ratio
                  <select
                    value={cropPresetKey}
                    onChange={(e) => setCropPresetKey(e.target.value)}
                    className="w-full border rounded px-2 py-1 mt-1"
                  >
                    {Object.values(CROP_PRESETS).map((preset) => (
                      <option key={preset.key} value={preset.key}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-slate-700">
                  Crop mode
                  <select
                    value={cropMode}
                    onChange={(e) => setCropMode(e.target.value)}
                    className="w-full border rounded px-2 py-1 mt-1"
                  >
                    <option value="cover">Fill frame (crop extra)</option>
                    <option value="contain">Fit full image (white space if needed)</option>
                  </select>
                </label>
                <label className="text-sm text-slate-700">
                  Output size
                  <input
                    type="range"
                    min="40"
                    max="100"
                    step="5"
                    value={cropOutputScale}
                    onChange={(e) => setCropOutputScale(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    {cropOutputScale}% ({outputWidth} x {outputHeight})
                  </div>
                </label>
                <label className="text-sm text-slate-700">
                  Horizontal Focus
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={cropX}
                    onChange={(e) => setCropX(Number(e.target.value))}
                    className="w-full"
                  />
                </label>
                <label className="text-sm text-slate-700">
                  Vertical Focus
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={cropY}
                    onChange={(e) => setCropY(Number(e.target.value))}
                    className="w-full"
                  />
                </label>
                <label className="text-sm text-slate-700">
                  Zoom
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.01"
                    value={cropZoom}
                    onChange={(e) => setCropZoom(Number(e.target.value))}
                    className="w-full"
                  />
                </label>
                <label className="text-sm text-slate-700">
                  Crop Left
                  <input
                    type="range"
                    min="0"
                    max="45"
                    step="1"
                    value={cropTrimLeft}
                    onChange={(e) => handleHorizontalTrimChange("left", e.target.value)}
                    className="w-full"
                  />
                  <div className="text-xs text-slate-500 mt-1">{cropTrimLeft}%</div>
                </label>
                <label className="text-sm text-slate-700">
                  Crop Right
                  <input
                    type="range"
                    min="0"
                    max="45"
                    step="1"
                    value={cropTrimRight}
                    onChange={(e) => handleHorizontalTrimChange("right", e.target.value)}
                    className="w-full"
                  />
                  <div className="text-xs text-slate-500 mt-1">{cropTrimRight}%</div>
                </label>
                <label className="text-sm text-slate-700">
                  Crop Top
                  <input
                    type="range"
                    min="0"
                    max="45"
                    step="1"
                    value={cropTrimTop}
                    onChange={(e) => handleVerticalTrimChange("top", e.target.value)}
                    className="w-full"
                  />
                  <div className="text-xs text-slate-500 mt-1">{cropTrimTop}%</div>
                </label>
                <label className="text-sm text-slate-700">
                  Crop Bottom
                  <input
                    type="range"
                    min="0"
                    max="45"
                    step="1"
                    value={cropTrimBottom}
                    onChange={(e) => handleVerticalTrimChange("bottom", e.target.value)}
                    className="w-full"
                  />
                  <div className="text-xs text-slate-500 mt-1">{cropTrimBottom}%</div>
                </label>
                <label className="text-sm text-slate-700">
                  JPG quality
                  <input
                    type="range"
                    min="60"
                    max="100"
                    step="1"
                    value={cropQuality}
                    onChange={(e) => setCropQuality(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-xs text-slate-500 mt-1">{cropQuality}%</div>
                </label>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-slate-200 flex flex-wrap justify-between items-center gap-3">
              <button
                type="button"
                onClick={handleCancelCrop}
                className="px-4 py-2 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleUseOriginalImage}
                  disabled={isApplyingCrop}
                  className="px-4 py-2 rounded border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                >
                  Use Original
                </button>
                <button
                  type="button"
                  onClick={handleApplyCrop}
                  disabled={!cropImage || isApplyingCrop}
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isApplyingCrop ? "Applying..." : "Save Crop"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ManageContentSections;

